import { useEffect, useState, useContext } from 'react'
import moment from 'moment'

import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import {
  formatCurrency,
  formatDateTime,
  capitalizeString,
  createWorkbook,
  saveWorkbook,
  addWorksheet,
  setHeaders,
  setColumnsAndRows,
  styleTotalRow,
  currencyFormat,
  DEFAULTNUMHEADERS,
  EMPTY_DISPLAY_VALUE,
  checkPermission,
} from '../../utilities/helpers'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import { getARAgingSummaryReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'

import { ReportActions } from '../ReportActions'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'

export default function ARAgingSummaryWrapper({ organization }) {
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_AR_AGING_SUMMARY_REPORT
    )

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [asOfDate, setAsOfDate] = useState(null)
  const [rows, setRows] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!organization?.uuid) return
      if (!asOfDate) return
      setIsLoadingData(true)
      try {
        const res = await getARAgingSummaryReport(
          organization.uuid,
          asOfDate.toISOString()
        )
        setRows(res.data)
      } catch (error) {
        console.error('Error fetching A/R aging summary:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    if (!orgPermissionsReady || !hasPermission) return
    fetchData()
  }, [
    asOfDate,
    organization?.uuid,
    orgPermissionsReady,
    hasPermission,
    refreshToken,
  ])

  const handleExcelExport = async () => {
    try {
      if (!orgPermissionsReady || !hasPermission) return
      if (!rows || !rows.length) return

      const workbook = createWorkbook()
      const worksheet = addWorksheet('AR Aging Summary', workbook)

      const asOfDateStr = formatDateTime(moment(asOfDate), 'dateOnly')
      const headerLines = [`As of ${asOfDateStr}`]
      setHeaders(
        'A/R Aging Summary',
        organization?.name || '',
        worksheet,
        headerLines
      )

      const columns = [
        'customer',
        'payment_plan',
        'current',
        'one_to_thirty',
        'thirty_one_to_sixty',
        'sixty_one_to_ninety',
        'ninety_plus',
        'total',
      ]
      const formatRow = (row) => ({
        customer: capitalizeString(row.customer) || EMPTY_DISPLAY_VALUE,
        payment_plan: row.paymentPlan || EMPTY_DISPLAY_VALUE,
        current: Number(row.current ?? 0),
        one_to_thirty: Number(row.oneToThirty ?? 0),
        thirty_one_to_sixty: Number(row.thirtyOneToSixty ?? 0),
        sixty_one_to_ninety: Number(row.sixtyOneToNinety ?? 0),
        ninety_plus: Number(row.ninetyPlus ?? 0),
        total: Number(row.total ?? 0),
      })

      setColumnsAndRows(columns, rows, formatRow, worksheet)

      // setColumnsAndRows derives headers from keys via formatString ("one_to_thirty" → "One To Thirty").
      // Client request: use numeric range labels (same as the on-page table), not spelled-out words.
      const headerRows = DEFAULTNUMHEADERS + headerLines.length
      const headerRow = worksheet.getRow(headerRows)
      ;[
        'Customer',
        'Payment Plan',
        'Current',
        '1 - 30',
        '31 - 60',
        '61 - 90',
        '91 and over',
        'Total',
      ].forEach((label, idx) => {
        headerRow.getCell(idx + 1).value = label
      })

      for (let col = 3; col <= 8; col++) {
        currencyFormat(col, worksheet, headerRows)
      }

      const lastRow = worksheet.lastRow
      styleTotalRow(lastRow, worksheet, false)

      await saveWorkbook(workbook, 'ar-aging-summary')
    } catch (err) {
      console.error('A/R Aging Summary Excel export failed:', err)
    }
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  return (
    <section
      className="spacer-sm"
      data-testid="ar-aging-summary-report-section"
    >
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>A/R Aging Summary Report</h1>
        </div>
        <p className="section-header-desc">
          View outstanding balances grouped by customer and aging bucket.
        </p>
      </header>

      <ReportActions
        isLoading={isLoadingData}
        onRefresh={refresh}
        isRefreshing={isLoadingData}
        selected={asOfDate}
        startDate={asOfDate}
        setStartDate={setAsOfDate}
        showTime={false}
        data={rows}
        downloadReport={handleExcelExport}
      />

      <Results isLoading={isLoadingData} data={rows} asOfDate={asOfDate} />
    </section>
  )
}

const TableHeader = () => (
  <thead>
    <tr className="md">
      <th className="text-left uppercase text-body">Customer</th>
      <th className="text-left uppercase text-body">Payment Plan</th>
      <th className="text-end uppercase text-body">Current</th>
      <th className="text-end uppercase text-body">1 - 30</th>
      <th className="text-end uppercase text-body">31 - 60</th>
      <th className="text-end uppercase text-body">61 - 90</th>
      <th className="text-end uppercase text-body">91 and over</th>
      <th className="text-end uppercase text-body">Total</th>
    </tr>
  </thead>
)

const Row = ({ row }) => {
  const isTotal = row.customer === 'TOTAL'
  return (
    <tr className={`md ${isTotal ? 'total-row dark' : ''} `}>
      <td className="text-left">
        {capitalizeString(row.customer) || EMPTY_DISPLAY_VALUE}
      </td>
      <td className="text-left">
        {isTotal ? EMPTY_DISPLAY_VALUE : row.paymentPlan || EMPTY_DISPLAY_VALUE}
      </td>
      <td className="text-end">{formatCurrency(row.current)}</td>
      <td className="text-end">{formatCurrency(row.oneToThirty)}</td>
      <td className="text-end">{formatCurrency(row.thirtyOneToSixty)}</td>
      <td className="text-end">{formatCurrency(row.sixtyOneToNinety)}</td>
      <td className="text-end">{formatCurrency(row.ninetyPlus)}</td>
      <td className="text-end">{formatCurrency(row.total)}</td>
    </tr>
  )
}

function Results({ isLoading, data, asOfDate }) {
  if (!asOfDate) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing an as of date</p>
          <p className="subtitle subtitle--dark">
            Select a date above and the report will appear right away.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (!data || data?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No results found for that date.</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <div className="card-body-heading card-body-heading--xs">
        <Card.Title as="h5">A/R Aging Summary Report</Card.Title>
        <Card.Subtitle className="text-muted mt-2 fst-italic">
          As of {formatDateTime(moment(asOfDate), 'dateOnly')}
        </Card.Subtitle>
      </div>
      <div className="table-container border-bottom-0">
        <div className="table-container--fixed-height--scrollable">
          <Table responsive>
            <TableHeader />
            <tbody>
              {data.map((row, index) => (
                <Row key={`${row.customer}-${index}`} row={row} />
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </Card>
  )
}
